import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    Authorization: `Bearer ${token}`
  };
};

// Utility function to extract error message from FastAPI responses
export const getErrorMessage = (error, defaultMessage = 'Error desconocido') => {
  if (!error.response?.data?.detail) {
    return error.message || defaultMessage;
  }
  
  const detail = error.response.data.detail;
  
  if (typeof detail === 'string') {
    return detail;
  }
  
  if (Array.isArray(detail)) {
    // Pydantic validation error returns array of objects with {type, loc, msg, input, url}
    return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
  }
  
  if (typeof detail === 'object') {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  
  return defaultMessage;
};

// Balance APIs
export const getBalance = async () => {
  const response = await axios.get(`${API}/balance`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const createBalanceRequest = async (amount, reference, proof_image_base64 = null) => {
  const response = await axios.post(
    `${API}/balance/request`,
    { amount, reference, proof_image_base64 },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getBalanceRequests = async () => {
  const response = await axios.get(`${API}/balance/requests`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// External service requests (IPTV / Remesa - manual admin processing)
export const getMyExternalRequests = async (statusFilter = null) => {
  const params = {};
  if (statusFilter) params.status_filter = statusFilter;
  const response = await axios.get(`${API}/external-requests`, {
    headers: getAuthHeaders(),
    params,
  });
  return response.data;
};

export const getAdminExternalRequests = async (statusFilter = 'pendiente', serviceType = null) => {
  const params = {};
  if (statusFilter) params.status_filter = statusFilter;
  if (serviceType) params.service_type = serviceType;
  const response = await axios.get(`${API}/admin/external-requests`, {
    headers: getAuthHeaders(),
    params,
  });
  return response.data;
};

export const approveExternalRequest = async (requestId, providerReference = null, adminNotes = null, assignedNumber = null) => {
  const response = await axios.post(
    `${API}/admin/external-requests/${requestId}/approve`,
    { provider_reference: providerReference, admin_notes: adminNotes, assigned_number: assignedNumber },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// ─── SIM Card Serials Inventory (admin) ──────────────────────────────────
export const assignSimSerials = async (userId, carrier, serials) => {
  const response = await axios.post(
    `${API}/admin/simcards/assign`,
    { user_id: userId, carrier, serials },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const listSimSerials = async (params = {}) => {
  const qs = new URLSearchParams();
  if (params.user_id) qs.set('user_id', params.user_id);
  if (params.carrier) qs.set('carrier', params.carrier);
  if (params.status) qs.set('status', params.status);
  const response = await axios.get(
    `${API}/admin/simcards/list?${qs.toString()}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const deleteSimSerial = async (serialId) => {
  const response = await axios.delete(
    `${API}/admin/simcards/${serialId}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const rejectExternalRequest = async (requestId, reason, adminNotes = null) => {
  const response = await axios.post(
    `${API}/admin/external-requests/${requestId}/reject`,
    { reason, admin_notes: adminNotes },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const returnExternalRequestForCorrection = async (requestId, reason, adminNotes = null) => {
  const response = await axios.post(
    `${API}/admin/external-requests/${requestId}/return-for-correction`,
    { reason, admin_notes: adminNotes },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// Admin Balance APIs
export const getAllBalanceRequests = async (status = null, directOnly = false) => {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (directOnly) params.set('direct_only', 'true');
  const qs = params.toString();
  const url = qs ? `${API}/admin/balance/requests?${qs}` : `${API}/admin/balance/requests`;
  const response = await axios.get(url, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const approveBalanceRequest = async (requestId, adminNotes = null, paymentReference = null, adjustedAmount = null, confirmDuplicateRef = false) => {
  const response = await axios.put(
    `${API}/admin/balance/approve/${requestId}`,
    {
      status: 'aprobado',
      admin_notes: adminNotes,
      payment_reference: paymentReference,
      adjusted_amount: adjustedAmount,
      confirm_duplicate_ref: !!confirmDuplicateRef,
    },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const rejectBalanceRequest = async (requestId, adminNotes = null) => {
  const response = await axios.put(
    `${API}/admin/balance/reject/${requestId}`,
    { status: 'rechazado', admin_notes: adminNotes },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// Delegate a reseller-client balance request to its parent reseller so the
// admin no longer sees it but the reseller still manages it.
export const delegateBalanceRequestToReseller = async (requestId) => {
  const response = await axios.post(
    `${API}/admin/balance-requests/${requestId}/delegate-to-reseller`,
    {},
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// Transaction APIs
export const createTransaction = async (transactionData) => {
  const response = await axios.post(
    `${API}/services/transaction`,
    transactionData,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getTransactions = async () => {
  const response = await axios.get(`${API}/transactions`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getTransaction = async (transactionId) => {
  const response = await axios.get(`${API}/transactions/${transactionId}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Bank Account APIs
export const getBankAccounts = async () => {
  const response = await axios.get(`${API}/bank-accounts`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const createBankAccount = async (accountData) => {
  const response = await axios.post(
    `${API}/admin/bank-accounts`,
    accountData,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const updateBankAccount = async (accountId, accountData) => {
  const response = await axios.put(
    `${API}/admin/bank-accounts/${accountId}`,
    accountData,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const deleteBankAccount = async (accountId) => {
  const response = await axios.delete(
    `${API}/admin/bank-accounts/${accountId}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// System Config APIs
export const getSystemConfig = async () => {
  const response = await axios.get(`${API}/config`);
  return response.data;
};

export const updateSystemConfig = async (configData) => {
  const response = await axios.put(
    `${API}/admin/config`,
    configData,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// Gift Cards admin config APIs
export const getGiftCardsAdminConfig = async () => {
  const response = await axios.get(`${API}/giftcards/admin/config`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const updateGiftCardsAdminConfig = async (payload) => {
  const response = await axios.put(`${API}/giftcards/admin/config`, payload, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const syncBcrdRate = async () => {
  const response = await axios.post(`${API}/giftcards/admin/sync-bcrd`, {}, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Juegos (Direct game topups - Free Fire LATAM)
export const getFazerGames = async () => {
  const response = await axios.get(`${API}/juegos/games`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getFazerProducts = async (gameId, region = null) => {
  const params = { game_id: gameId };
  if (region) params.region = region;
  const response = await axios.get(`${API}/juegos/products`, {
    params,
    headers: getAuthHeaders()
  });
  return response.data;
};

export const validateFazerUid = async (game, userId, server = null, region = null) => {
  const payload = { game, user_id: userId };
  if (server) payload.server = server;
  if (region) payload.region = region;
  const response = await axios.post(`${API}/juegos/validate-uid`, payload, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const fazerTopup = async ({ game_id, product_id, user_id, server = null, nickname = null, region = null }) => {
  const payload = { game_id, product_id, user_id };
  if (server) payload.server = server;
  if (nickname) payload.nickname = nickname;
  if (region) payload.region = region;
  const response = await axios.post(`${API}/juegos/topup`, payload, {
    headers: getAuthHeaders(),
    timeout: 60000,
  });
  return response.data;
};

export const getFazerOrderStatus = async (reference) => {
  const response = await axios.get(`${API}/juegos/order/${reference}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Admin
export const getFazerBalance = async () => {
  const response = await axios.get(`${API}/juegos/balance`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getFazerAdminConfig = async () => {
  const response = await axios.get(`${API}/juegos/admin/config`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const updateFazerAdminConfig = async (payload) => {
  const response = await axios.put(`${API}/juegos/admin/config`, payload, {
    headers: getAuthHeaders()
  });
  return response.data;
};


// Gift Cards brand visibility
export const getGiftCardBrands = async () => {
  const response = await axios.get(`${API}/giftcards/admin/brands`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const refreshGiftCardBrands = async () => {
  const response = await axios.post(`${API}/giftcards/admin/brands/refresh`, {}, {
    headers: getAuthHeaders(),
    timeout: 90000,
  });
  return response.data;
};

export const updateDisabledGiftCardBrands = async (disabledBrandIds) => {
  const response = await axios.put(
    `${API}/giftcards/admin/brands/disabled`,
    { disabled_brand_ids: disabledBrandIds },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getNonMidasEarnings = async (dateFrom, dateTo) => {
  const params = {};
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
  const response = await axios.get(`${API}/admin/earnings/non-midas`, {
    headers: getAuthHeaders(),
    params,
  });
  return response.data;
};

// Get admin balance for sub-admin
export const getAdminBalanceForSubadmin = async () => {
  const response = await axios.get(`${API}/subadmin/admin-balance`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Admin Stats APIs
export const getAdminStats = async () => {
  const response = await axios.get(`${API}/admin/stats`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Admin Transactions API
export const getAllTransactions = async (limit = 500, skip = 0, status = null, userId = null, search = null, dateFrom = null, dateTo = null) => {
  let url = `${API}/admin/transactions?limit=${limit}&skip=${skip}`;
  if (status) url += `&status=${status}`;
  if (userId) url += `&user_id=${userId}`;
  if (search) url += `&search=${encodeURIComponent(search)}`;
  if (dateFrom) url += `&date_from=${dateFrom}`;
  if (dateTo) url += `&date_to=${dateTo}`;
  const response = await axios.get(url, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getAllUsers = async () => {
  const response = await axios.get(`${API}/admin/users`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const updateUserByAdmin = async (userId, userData) => {
  const response = await axios.put(
    `${API}/admin/users/${userId}`,
    userData,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const changeUserPasswordByAdmin = async (userId, newPassword) => {
  const response = await axios.put(
    `${API}/admin/users/${userId}/password`,
    { new_password: newPassword },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const changeUserRoleByAdmin = async (userId, newRole) => {
  const response = await axios.put(
    `${API}/admin/users/${userId}/role`,
    { new_role: newRole },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const changeUserCedulaByAdmin = async (userId, newCedula) => {
  const response = await axios.put(
    `${API}/admin/users/${userId}/cedula`,
    { new_cedula: newCedula },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const createUserByAdmin = async (userData) => {
  const response = await axios.post(
    `${API}/admin/users`,
    userData,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// Reseller approval endpoints
export const getPendingResellers = async () => {
  const response = await axios.get(`${API}/admin/pending-resellers`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const approveReseller = async (userId) => {
  const response = await axios.put(
    `${API}/admin/users/${userId}/approve`,
    {},
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const rejectReseller = async (userId) => {
  const response = await axios.put(
    `${API}/admin/users/${userId}/reject`,
    {},
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const deleteUserByAdmin = async (userId) => {
  const response = await axios.delete(
    `${API}/admin/users/${userId}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const changeUserBalanceByAdmin = async (userId, amount, operation, notes = null, idempotencyKey = null, confirmDuplicate = false, securityRef = null, confirmDuplicateRef = false) => {
  // Generate an idempotency key when the caller didn't provide one so a
  // retry on a dropped connection can never double-credit the user.
  const key = idempotencyKey || (
    (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `ibk-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  );
  const response = await axios.put(
    `${API}/admin/users/${userId}/balance`,
    {
      amount, operation, notes,
      idempotency_key: key,
      confirm_duplicate: !!confirmDuplicate,
      security_ref: securityRef || null,
      confirm_duplicate_ref: !!confirmDuplicateRef,
    },
    { headers: getAuthHeaders(), timeout: 30000 }
  );
  return response.data;
};

// === Per-user Service Blocking ===
export const getUserBlockedServices = async (userId) => {
  const response = await axios.get(
    `${API}/admin/users/${userId}/blocked-services`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const setUserBlockedServices = async (userId, blockedServices) => {
  const response = await axios.put(
    `${API}/admin/users/${userId}/blocked-services`,
    { blocked_services: blockedServices },
    { headers: getAuthHeaders() }
  );
  return response.data;
};


export const getBalanceHistory = async () => {
  const response = await axios.get(`${API}/admin/balance-history`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getUserBalanceHistory = async () => {
  const response = await axios.get(`${API}/user/balance-history`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Note: Old service-based commission APIs removed - now using provider-based commissions in ADMIN RESELLER COMMISSIONS section

// ==================== MESSAGE APIs ====================

export const createMessage = async (messageData) => {
  const response = await axios.post(`${API}/admin/messages`, messageData, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getAdminMessages = async () => {
  const response = await axios.get(`${API}/admin/messages`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const toggleMessage = async (messageId) => {
  const response = await axios.put(`${API}/admin/messages/${messageId}/toggle`, {}, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const deleteMessage = async (messageId) => {
  const response = await axios.delete(`${API}/admin/messages/${messageId}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getPendingMessages = async () => {
  const response = await axios.get(`${API}/admin/messages/pending`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const approveMessage = async (messageId) => {
  const response = await axios.put(`${API}/admin/messages/${messageId}/approve`, {}, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getUserMessages = async () => {
  const response = await axios.get(`${API}/user/messages`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const markMessageAsRead = async (messageId) => {
  const response = await axios.post(`${API}/user/messages/${messageId}/read`, {}, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const deleteUserMessage = async (messageId) => {
  const response = await axios.delete(`${API}/user/messages/${messageId}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// ==================== RESELLER APIs ====================

export const getResellerStats = async () => {
  const response = await axios.get(`${API}/reseller/stats`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// ─── Reseller settings (e.g. hide clients balance) ───────────────────────
export const getResellerSettings = async () => {
  const response = await axios.get(`${API}/reseller/settings`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const setResellerHideClientsBalance = async (hide) => {
  const response = await axios.put(
    `${API}/reseller/settings/hide-clients-balance`,
    { hide_clients_balance: !!hide },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getResellerClients = async () => {
  const response = await axios.get(`${API}/reseller/clients`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const createResellerClient = async (clientData) => {
  const response = await axios.post(
    `${API}/reseller/clients`,
    clientData,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const updateResellerClient = async (clientId, clientData) => {
  const response = await axios.put(
    `${API}/reseller/clients/${clientId}`,
    clientData,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const changeResellerClientPassword = async (clientId, newPassword) => {
  const response = await axios.put(
    `${API}/reseller/clients/${clientId}/password`,
    { new_password: newPassword },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const transferBalanceToClient = async (clientId, amount, operation = 'add', notes = null) => {
  const response = await axios.post(
    `${API}/reseller/clients/${clientId}/balance`,
    { amount, operation, notes },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const deleteResellerClient = async (clientId) => {
  const response = await axios.delete(
    `${API}/reseller/clients/${clientId}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getResellerTransfers = async () => {
  const response = await axios.get(`${API}/reseller/transfers`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// ==================== RESELLER BALANCE REQUESTS APIs ====================

export const getResellerBalanceRequests = async (status = null) => {
  const url = status ? `${API}/reseller/balance-requests?status=${status}` : `${API}/reseller/balance-requests`;
  const response = await axios.get(url, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const approveResellerBalanceRequest = async (requestId, adminNotes = null, paymentReference = null) => {
  const response = await axios.put(
    `${API}/reseller/balance/approve/${requestId}`,
    { status: 'aprobado', admin_notes: adminNotes, payment_reference: paymentReference },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const rejectResellerBalanceRequest = async (requestId, adminNotes = null) => {
  const response = await axios.put(
    `${API}/reseller/balance/reject/${requestId}`,
    { status: 'rechazado', admin_notes: adminNotes },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// ==================== RESELLER BANK ACCOUNTS APIs ====================

export const getResellerBankAccounts = async () => {
  const response = await axios.get(`${API}/reseller/bank-accounts`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const createResellerBankAccount = async (accountData) => {
  const response = await axios.post(
    `${API}/reseller/bank-accounts`,
    accountData,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const updateResellerBankAccount = async (accountId, accountData) => {
  const response = await axios.put(
    `${API}/reseller/bank-accounts/${accountId}`,
    accountData,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const deleteResellerBankAccount = async (accountId) => {
  const response = await axios.delete(
    `${API}/reseller/bank-accounts/${accountId}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// ==================== ADMIN RESELLER COMMISSIONS APIs ====================

export const getResellerCommissions = async (resellerId) => {
  const response = await axios.get(`${API}/admin/resellers/${resellerId}/commissions`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const setResellerCommissions = async (resellerId, providerCommissions) => {
  const response = await axios.put(
    `${API}/admin/resellers/${resellerId}/commissions`,
    { provider_commissions: providerCommissions },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const resetResellerCommissions = async (resellerId) => {
  const response = await axios.delete(`${API}/admin/resellers/${resellerId}/commissions`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// ==================== RESELLER CLIENT COMMISSIONS APIs ====================

export const getClientCommissions = async (clientId) => {
  const response = await axios.get(`${API}/reseller/clients/${clientId}/commissions`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const setClientCommissions = async (clientId, providerCommissions) => {
  const response = await axios.put(
    `${API}/reseller/clients/${clientId}/commissions`,
    { provider_commissions: providerCommissions },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const resetClientCommissions = async (clientId) => {
  const response = await axios.delete(`${API}/reseller/clients/${clientId}/commissions`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// ==================== RESELLER EARNINGS APIs ====================

export const getResellerEarnings = async (limit = 100, skip = 0, dateFrom = null, dateTo = null) => {
  let url = `${API}/reseller/earnings?limit=${limit}&skip=${skip}`;
  if (dateFrom) url += `&date_from=${dateFrom}`;
  if (dateTo) url += `&date_to=${dateTo}`;
  const response = await axios.get(url, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getResellerEarningsSummary = async (dateFrom = null, dateTo = null) => {
  let url = `${API}/reseller/earnings/summary`;
  const params = [];
  if (dateFrom) params.push(`date_from=${dateFrom}`);
  if (dateTo) params.push(`date_to=${dateTo}`);
  if (params.length > 0) url += `?${params.join('&')}`;
  const response = await axios.get(url, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getResellerClientTransactions = async (limit = 100, skip = 0, dateFrom = null, dateTo = null, clientId = null, status = null) => {
  let url = `${API}/reseller/clients/transactions?limit=${limit}&skip=${skip}`;
  if (dateFrom) url += `&date_from=${dateFrom}`;
  if (dateTo) url += `&date_to=${dateTo}`;
  if (clientId) url += `&client_id=${encodeURIComponent(clientId)}`;
  if (status) url += `&tx_status=${encodeURIComponent(status)}`;
  const response = await axios.get(url, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// ==================== SUB-ADMIN APIs ====================

export const createSubAdmin = async (subAdminData) => {
  const response = await axios.post(`${API}/admin/sub-admins`, subAdminData, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getAllSubAdmins = async () => {
  const response = await axios.get(`${API}/admin/sub-admins`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const deleteSubAdmin = async (userId) => {
  const response = await axios.delete(`${API}/admin/sub-admins/${userId}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getAuditLogs = async (limit = 100, dateFrom = null, dateTo = null) => {
  let url = `${API}/admin/audit-logs?limit=${limit}`;
  if (dateFrom) url += `&date_from=${dateFrom}`;
  if (dateTo) url += `&date_to=${dateTo}`;
  const response = await axios.get(url, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getSubAdminAuditLogs = async (userId, limit = 100) => {
  const response = await axios.get(`${API}/admin/audit-logs/${userId}?limit=${limit}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const createClientBySubAdmin = async (clientData) => {
  const response = await axios.post(`${API}/sub-admin/clients`, clientData, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getMyAuditLogs = async (limit = 50) => {
  const response = await axios.get(`${API}/sub-admin/my-audit-logs?limit=${limit}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// ==================== REPORTS APIs ====================

export const getSalesReport = async (period = 'month', startDate = null, endDate = null) => {
  let url = `${API}/reports/sales?period=${period}`;
  if (startDate && endDate) {
    url += `&start_date=${startDate}&end_date=${endDate}`;
  }
  const response = await axios.get(url, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// ==================== TRANSACTION ANNULMENT API ====================

export const annulTransaction = async (transactionId) => {
  const response = await axios.post(
    `${API}/transactions/${transactionId}/cancel`,
    {},
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// Admin force cancel transaction (no time limit)
export const adminForceCancelTransaction = async (transactionRef) => {
  const response = await axios.post(
    `${API}/admin/transactions/${transactionRef}/force-cancel`,
    {},
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// Admin verify transaction status in Midas
export const adminVerifyMidasStatus = async (transactionRef) => {
  const response = await axios.post(
    `${API}/admin/transactions/${transactionRef}/verify-midas-status`,
    {},
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// Admin sync all transactions with Midas
export const adminSyncWithMidas = async () => {
  const response = await axios.post(
    `${API}/admin/transactions/sync-with-midas`,
    {},
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// Get pending balance requests for admin notifications
export const getPendingBalanceRequestsForAdmin = async () => {
  const response = await axios.get(
    `${API}/admin/pending-balance-requests`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// Get pending balance requests for reseller notifications
export const getPendingBalanceRequestsForReseller = async () => {
  const response = await axios.get(
    `${API}/reseller/pending-balance-requests`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// Get popup notifications (important alerts like balance approved)
export const getPopupNotifications = async () => {
  const response = await axios.get(
    `${API}/user/popup-notifications`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// Mark popup notification as shown
export const markPopupAsShown = async (messageId) => {
  const response = await axios.post(
    `${API}/user/popup-notifications/${messageId}/shown`,
    {},
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// ==================== MIDAS API FUNCTIONS ====================

// Check Midas API connection status
export const getMidasStatus = async () => {
  const response = await axios.get(`${API}/midas/status`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Check Alt provider (UTP) connection status
export const getAltStatus = async () => {
  const response = await axios.get(`${API}/alt/status`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Get all providers available in Midas (topup + data + services)
export const getAllMidasProviders = async () => {
  const response = await axios.get(`${API}/midas/providers/all`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Get Midas login status (admin only)
export const getMidasLoginStatus = async () => {
  const response = await axios.get(`${API}/midas/login-status`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Request manual login to Midas (admin only)
export const requestMidasLogin = async (force = false) => {
  const response = await axios.post(`${API}/midas/request-login`, { force }, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// ==================== MAINTENANCE MODE ====================

// Get maintenance mode status
export const getMaintenanceStatus = async () => {
  const response = await axios.get(`${API}/maintenance/status`);
  return response.data;
};

// Toggle maintenance mode (admin only)
export const toggleMaintenanceMode = async (enabled, message = null) => {
  const response = await axios.post(`${API}/admin/maintenance/toggle`, 
    { enabled, message },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// Get topup providers (mobile recharges)
export const getMidasTopupProviders = async () => {
  const response = await axios.get(`${API}/midas/providers/topup`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Get data package providers
export const getMidasDataProviders = async () => {
  const response = await axios.get(`${API}/midas/providers/data`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Get bill payment providers
export const getMidasServiceProviders = async () => {
  const response = await axios.get(`${API}/midas/providers/services`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Get prepaid electricity providers (honors admin override to Alt provider,
// e.g. Edesur Prepago via UTP when Midas is down or overridden).
export const getMidasPrepaidProviders = async () => {
  const response = await axios.get(`${API}/midas/providers/prepaid`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Get data plans for a provider
export const getMidasDataPlans = async (provider, phone) => {
  const response = await axios.post(
    `${API}/midas/data/plans`,
    { provider, phone: phone || '' },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// Get prepaid electricity info (Edenorte/Edesur prepago)
export const getMidasPrepaidInfo = async (provider, account) => {
  const response = await axios.post(
    `${API}/midas/prepaid/info`,
    { provider, account },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// Get pending invoices
export const getMidasInvoices = async (provider, account) => {
  const response = await axios.post(
    `${API}/midas/invoices`,
    { provider, account },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// Process a topup (mobile recharge or prepaid electricity)
export const processMidasTopup = async (provider, destination, amount, serviceType = 'recarga_movil') => {
  const response = await axios.post(
    `${API}/midas/topup`,
    { 
      provider, 
      destination, 
      amount,
      service_type: serviceType 
    },
    { headers: getAuthHeaders(), timeout: 60000 }
  );
  return response.data;
};

// Process a bill payment
export const processMidasPayment = async (provider, account, amount, invoiceId = null) => {
  const response = await axios.post(
    `${API}/midas/service/pay`,
    { provider, account, amount, invoice_id: invoiceId },
    { headers: getAuthHeaders(), timeout: 60000 }
  );
  return response.data;
};

// Process a data package purchase
export const processMidasDataPackage = async (provider, destination, planId, amount, planName) => {
  const response = await axios.post(
    `${API}/midas/data/purchase`,
    { 
      provider, 
      destination, 
      plan_id: String(planId), // Ensure plan_id is always a string
      amount,
      plan_name: planName || ""
    },
    { headers: getAuthHeaders(), timeout: 60000 }
  );
  return response.data;
};

// Recovery for timed-out client requests. Finds the most recent matching TX
// for the user and returns its authoritative status, actively querying UTP
// (`QueryTransactionsByReference`) to promote pendiente → completado when
// possible. Returns { found, status, reference, commission, amount_charged, ... }.
export const recoverRecentTransaction = async ({ provider, phone_number, amount, service_type, since_seconds = 180 }) => {
  const response = await axios.post(
    `${API}/transactions/recover-recent`,
    { provider, phone_number, amount, service_type, since_seconds },
    { headers: getAuthHeaders(), timeout: 30000 }
  );
  return response.data;
};

// Check transaction status
export const checkMidasTransactionStatus = async (transactionId) => {
  const response = await axios.post(
    `${API}/midas/transaction/${transactionId}/status`,
    {},
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// Reverse/annul a transaction via Midas
export const reverseMidasTransaction = async (transactionId) => {
  const response = await axios.post(
    `${API}/midas/transaction/${transactionId}/reverse`,
    {},
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// Verify and recover a failed transaction via Midas
export const verifyAndRecoverTransaction = async (transactionId) => {
  const response = await axios.post(
    `${API}/midas/transaction/${transactionId}/verify-and-recover`,
    {},
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// ==================== SERVICES STATUS APIs ====================

export const getServicesStatus = async () => {
  const response = await axios.get(`${API}/config/services-status`);
  return response.data;
};

// ==================== LOGO MANAGEMENT APIs ====================

export const getLogos = async () => {
  const response = await axios.get(`${API}/config/logos`);
  return response.data;
};

export const uploadLogo = async (logoType, imageBase64) => {
  const response = await axios.post(
    `${API}/admin/logos/upload`,
    { logo_type: logoType, image_base64: imageBase64 },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const deleteLogo = async (logoType) => {
  const response = await axios.delete(
    `${API}/admin/logos/${logoType}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// ==================== API CREDENTIALS MANAGEMENT ====================

export const getApiCredentials = async () => {
  const response = await axios.get(
    `${API}/admin/api-credentials`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const updateApiCredentials = async (credentials) => {
  const response = await axios.put(
    `${API}/admin/api-credentials`,
    credentials,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const testApiConnection = async () => {
  const response = await axios.post(
    `${API}/admin/api-credentials/test`,
    {},
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// ==================== INTERNATIONAL RECHARGES APIs ====================

export const getMidasInternationalProviders = async () => {
  const response = await axios.get(`${API}/midas/providers/international`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const processMidasInternationalTopup = async (provider, destination, amount, countryCode) => {
  const response = await axios.post(
    `${API}/midas/international/topup`,
    { provider, destination, amount, country_code: countryCode },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// ==================== INTERNATIONAL DATA PACKAGES APIs ====================

export const getInternationalDataCountries = async () => {
  const response = await axios.get(`${API}/midas/international/data/countries`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getInternationalDataProducts = async (countryCode) => {
  const response = await axios.get(`${API}/midas/international/data/products/${countryCode}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const processInternationalDataPackage = async (productId, number, countryCode, productName, productPrice) => {
  const response = await axios.post(
    `${API}/midas/international/data`,
    { 
      product_id: productId, 
      number, 
      country_code: countryCode,
      product_name: productName,
      product_price: productPrice
    },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// ==================== LOTTERY APIs ====================

export const getLotteryProviders = async () => {
  const response = await axios.get(`${API}/midas/lottery/providers`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getLotteryGames = async (provider) => {
  const response = await axios.post(
    `${API}/midas/lottery/games`,
    { provider },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const placeLotteryBet = async (data) => {
  const response = await axios.post(
    `${API}/midas/lottery/bet`,
    data,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// ==================== MIDAS INSURANCE APIs ====================

export const getMidasInsuranceVehicleTypes = async () => {
  const response = await axios.get(`${API}/midas/insurance/vehicle-types`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getMidasInsurancePlans = async (vehicleType) => {
  const response = await axios.get(`${API}/midas/insurance/plans/${vehicleType}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getMidasInsurancePrices = async () => {
  const response = await axios.get(`${API}/midas/insurance/prices`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const purchaseMidasInsurance = async (data) => {
  const response = await axios.post(
    `${API}/midas/insurance/purchase`,
    data,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const cancelMidasInsurance = async (transactionId) => {
  const response = await axios.post(
    `${API}/midas/insurance/${transactionId}/cancel`,
    {},
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getCustomerMidasInsurances = async (document) => {
  const response = await axios.get(`${API}/midas/insurance/customer/${document}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// ==================== MIDAS COMMISSIONS ====================

export const getMidasCommissions = async () => {
  const response = await axios.get(`${API}/admin/midas-commissions`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const updateMidasCommissions = async (midasCommissions, altCommissions = null) => {
  const payload = {};
  if (midasCommissions) payload.midas_commissions = midasCommissions;
  if (altCommissions) payload.alt_commissions = altCommissions;
  const response = await axios.put(`${API}/admin/midas-commissions`, payload, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getMarginReport = async (startDate = null, endDate = null) => {
  let url = `${API}/admin/margin-report`;
  const params = new URLSearchParams();
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  if (params.toString()) url += `?${params.toString()}`;
  
  const response = await axios.get(url, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Admin Earnings (Cascade Commissions)
export const getAdminEarnings = async (limit = 100, skip = 0, dateFrom = null, dateTo = null) => {
  let url = `${API}/admin/earnings?limit=${limit}&skip=${skip}`;
  if (dateFrom) url += `&date_from=${dateFrom}`;
  if (dateTo) url += `&date_to=${dateTo}`;
  const response = await axios.get(url, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getAdminEarningsSummary = async (dateFrom = null, dateTo = null) => {
  let url = `${API}/admin/earnings/summary`;
  const params = [];
  if (dateFrom) params.push(`date_from=${dateFrom}`);
  if (dateTo) params.push(`date_to=${dateTo}`);
  if (params.length > 0) url += `?${params.join('&')}`;
  const response = await axios.get(url, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Admin Balance Summary (Reconciliation)
export const getAdminBalanceSummary = async () => {
  const response = await axios.get(`${API}/admin/balance-summary`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Commission Settlement (Liquidación)
export const getPendingCommissions = async (cutoffDate = null) => {
  let url = `${API}/admin/commission-settlement/pending`;
  if (cutoffDate) {
    url += `?cutoff_date=${cutoffDate}`;
  }
  const response = await axios.get(url, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const settleCommissions = async (cutoffDate, notes = null) => {
  const response = await axios.post(`${API}/admin/commission-settlement/settle`, {
    cutoff_date: cutoffDate,
    notes: notes
  }, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getSettlementHistory = async (limit = 20) => {
  const response = await axios.get(`${API}/admin/commission-settlement/history?limit=${limit}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// System Logs (Admin only)
export const getSystemLogs = async (lines = 100, filter = '') => {
  const params = new URLSearchParams({ lines: lines.toString() });
  if (filter) params.append('filter', filter);
  
  const response = await axios.get(`${API}/admin/system-logs?${params}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};


// ==================== 2FA APIs ====================

export const verify2FA = async (cedula, code, method = 'totp') => {
  const response = await axios.post(`${API}/auth/2fa/verify`, {
    cedula,
    code,
    method
  });
  return response.data;
};

export const send2FAEmail = async (cedula) => {
  const response = await axios.post(`${API}/auth/2fa/send-email?cedula=${encodeURIComponent(cedula)}`);
  return response.data;
};

export const get2FAStatus = async () => {
  const response = await axios.get(`${API}/auth/2fa/status`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const setup2FA = async () => {
  const response = await axios.post(`${API}/auth/2fa/setup`, {}, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const confirm2FASetup = async (code) => {
  const response = await axios.post(`${API}/auth/2fa/confirm`, { code }, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const disable2FA = async (code) => {
  const response = await axios.post(`${API}/auth/2fa/disable?code=${encodeURIComponent(code)}`, {}, {
    headers: getAuthHeaders()
  });
  return response.data;
};
